import { useCallback, useEffect, useState } from 'react';

import { API } from '../utils/apiBase';

/**
 * Live products from the admin-managed MongoDB catalogue.
 * Returns docs as: { id, name, tagline, price, compare_at, status, media, ... }.
 *
 * Status drives UX:
 *  - 'available'   → regular product detail, Add to cart
 *  - 'preorder'    → preorder detail, deposit flow
 *  - 'sold-out'    → visible but Add to cart disabled
 *  - 'coming-soon' → tile only, no detail page
 *
 * Closed-system: refetches automatically when the tab regains focus,
 * becomes visible, or receives a `products:invalidate` event — so admin
 * edits (publish, hide, price change, media swap) land on the storefront
 * the moment the user returns from /admin.
 */
export const useStoreProducts = ({ status = null } = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let active = true;
    const url = `${API}/api/products?_=${Date.now()}${status ? `&status=${status}` : ''}`;
    fetch(url, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        setProducts(d.items || []);
        setLoading(false);
      })
      .catch(() => active && setLoading(false));
    return () => { active = false; };
  }, [status]);

  useEffect(() => {
    const cancel = load();
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('products:invalidate', onFocus);
    return () => {
      cancel && cancel();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('products:invalidate', onFocus);
    };
  }, [load]);

  return { products, loading };
};

export default useStoreProducts;
